import Container from "components/container";
import UserButton from "./user-button";
import NotificationsButton from "./notifications-button";
import LanguageSelect from "./language-select";
import LoginIcon from "@mui/icons-material/Login";
import { Button, Menu, MenuItem } from "@mui/material";
import { useDictionary } from "hooks/use-dictionary";
import IconButton from "components/icon-button";
import MenuButton from "./menu-button";
import Link from "components/link";
import { useAuth } from "providers/auth";
import { useImpersonation } from "providers/impersonation";
import { ROUTES } from "constants/routes";
import { DashboardLink } from "components/dashboard/layout";
import { FunctionComponent, MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { ReactComponent as IncognitoIcon } from "assets/incognito.svg";

interface HeaderProps {
  dashboardLinks?: DashboardLink[];
}

const showLanguageSwitcher = import.meta.env.REACT_APP_SHOW_LANGUAGE_SWITCHER === "true";

const Header: FunctionComponent<HeaderProps> = ({ dashboardLinks }) => {
  const dict = useDictionary();
  const { user } = useAuth();
  const { isImpersonating: isImpersonated, endImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const [informationalContentAnchor, setInformationalContentAnchor] = useState<null | HTMLElement>(null);
  const informationalContentMenuOpen = Boolean(informationalContentAnchor);

  const handleInformationalContentOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setInformationalContentAnchor(event.currentTarget);
  };

  const handleInformationalContentClose = () => {
    setInformationalContentAnchor(null);
  };

  const informationalContentLinks = [
    {
      title: dict.informationalContent.sections.HEALTHCARE_SPECIALISTS,
      href: ROUTES.informationalContent.healthcareSpecialists,
    },
    {
      title: dict.informationalContent.sections.HEALTHCARE_INSTITUTIONS,
      href: ROUTES.informationalContent.healthcareInstitutions,
    },
    {
      title: dict.informationalContent.sections.EDUCATION_PROVIDERS,
      href: ROUTES.informationalContent.educationProviders,
    },
  ];

  const links = [
    { title: dict.header.help, href: dict.header.helpUrl },
    { title: dict.header.informationalContent, href: ROUTES.informationalContent.healthcareSpecialists, children: informationalContentLinks },
        { title: dict.header.specialistAttractionProject, href: ROUTES.specialistAttractionProject },
    // TODO LSPSKP-6236: Restore when the specialist registry is ready for public access.
    // { title: dict.specialistLicenses.title, href: ROUTES.home + ROUTES.specialistLicenses },
  ];

  return (
    <div className="relative">
      <div
        className={classNames("bg-theme-primary flex items-center justify-center relative", {
          "h-4": !isImpersonated,
          "p-2 text-white z-[1301]": isImpersonated,
        })}
      >
        {isImpersonated && (
          <>
            <IncognitoIcon className="text-white mr-3" /> {dict.impersonate.header.impersonating}&nbsp;
            <strong>
              {user?.firstName} {user?.lastName}
            </strong>{" "}
            <span className="ml-6 text-white underline cursor-pointer" onClick={endImpersonation}>
              {dict.impersonate.header.endImpersonation}
            </span>
          </>
        )}
      </div>
      <Container className="shadow-[0px_2px_20px_0px_rgba(99,115,129,0.1)]">
        <div className="h-20 flex justify-between items-center">
          <Link to="/" className="flex gap-3 uppercase">
            <div className="text-theme-primary">VASPVT</div>
            <h1>
              <div>{dict.header.competencePlatform}</div>
            </h1>
          </Link>
          <div className="hidden lg:flex gap-6 px-10 items-center">
            <a href={links[0].href} className="text-theme-gray300" target="_blank" rel="noopener noreferrer">
              {links[0].title}
            </a>
            <Button
              id="informational-content-button"
              aria-controls={informationalContentMenuOpen ? "informational-content-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={informationalContentMenuOpen ? "true" : undefined}
              onClick={handleInformationalContentOpen}
              className="!text-theme-gray300 !normal-case !p-0 !min-w-0"
            >
              {dict.header.informationalContent}
            </Button>
            <Menu
              id="informational-content-menu"
              anchorEl={informationalContentAnchor}
              open={informationalContentMenuOpen}
              onClose={handleInformationalContentClose}
              MenuListProps={{ "aria-labelledby": "informational-content-button" }}
            >
              {informationalContentLinks.map((link) => (
                <MenuItem key={link.href} component="a" href={link.href} onClick={handleInformationalContentClose}>
                  {link.title}
                </MenuItem>
              ))}
            </Menu>
            <a href={links[2].href} className="text-theme-gray300">
              {links[2].title}
            </a>
            {showLanguageSwitcher && <LanguageSelect />}
            {user && <NotificationsButton user={user} />}
            {user && <UserButton />}
            {!user && (
              <IconButton aria-label={dict.header.login} onClick={() => navigate(ROUTES.login.home)}>
                <LoginIcon className="text-black items-center" />
              </IconButton>
            )}
          </div>
          <MenuButton
            className="lg:!hidden"
            links={links}
            dashboardLinks={dashboardLinks}
            showLanguageSwitcher={showLanguageSwitcher}
          />
        </div>
      </Container>
    </div>
  );
};

export default Header;
